import { getAuthToken } from './authStorage';
import {
  loadCompareList,
  saveCompareList,
  mergeCompareLists,
  shouldKeepCompareEmpty,
  markCompareCleared,
  clearCompareClearedFlag,
} from './compareProducts';

const normalizeUrl = (url) => {
  if (!url) return '';
  return url.replace(/\/+$/, '');
};

const API_BASE_URL = normalizeUrl(process.env.REACT_APP_API_URL);

const compareApiBase = () => `${API_BASE_URL ? `${API_BASE_URL}/api` : ''}/compare`;

export const canSyncCompare = () => Boolean(getAuthToken());

export const saveCompareToBackend = async (items) => {
  const token = getAuthToken();
  if (!token) return false;

  try {
    const response = await fetch(compareApiBase(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ items: items || [] }),
    });

    if (!response.ok) {
      console.error('Failed to save compare list:', response.statusText);
      return false;
    }
    return true;
  } catch (error) {
    console.error('Error saving compare list:', error);
    return false;
  }
};

export const loadUserCompare = async () => {
  const token = getAuthToken();
  if (!token) return null;

  try {
    const response = await fetch(`${compareApiBase()}/my-compare`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) return null;
    if (!response.ok) return null;

    const data = await response.json();
    return Array.isArray(data?.items) ? data.items : [];
  } catch (error) {
    console.error('Error loading compare list:', error);
    return null;
  }
};

/**
 * Merge account compare list with local storage, persist both, and POST to API.
 * Remote items come first; local-only items are appended (max 4 total).
 * If the user explicitly cleared compare, keep it empty and overwrite the server.
 */
export const syncCompareWithAccount = async () => {
  const localCompare = loadCompareList();
  if (!canSyncCompare()) {
    return localCompare;
  }

  if (shouldKeepCompareEmpty()) {
    saveCompareList([]);
    markCompareCleared();
    if (canSyncCompare()) {
      await saveCompareToBackend([]);
    }
    return [];
  }

  const remoteCompare = await loadUserCompare();
  const merged = mergeCompareLists(remoteCompare ?? [], localCompare);
  saveCompareList(merged);
  await saveCompareToBackend(merged);
  return merged;
};

let saveTimer = null;
export const debouncedSaveCompare = (items) => {
  saveCompareList(items);
  if (items.length > 0) {
    clearCompareClearedFlag();
  } else {
    markCompareCleared();
  }
  if (!canSyncCompare()) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveCompareToBackend(items);
  }, 800);
};

export const persistCompareNow = async (items) => {
  saveCompareList(items);
  if (!items.length) {
    markCompareCleared();
  } else {
    clearCompareClearedFlag();
  }
  if (!canSyncCompare()) return;
  if (saveTimer) clearTimeout(saveTimer);
  await saveCompareToBackend(items);
};
